/**
 * **`refereeForce`** — the GM override's door (`decisions/0209`, the `decisions/0042` amendment). A
 * `gm_force { intent }` skips the legality half of its family entirely (`refereeIntent.ts`'s
 * `checkIntentLegality` and the room's shared gate ladder in front of it, or a bespoke referee's own
 * legality half): this function runs a STRUCTURAL check only, then the same resolution half the
 * gameplay door runs, under the forced spend policy.
 *
 * **The forceable families.** The generic ones resolve through `resolveIntentPlan`: an attack,
 * a Barrage shot, a Full Tech pick, an activation (Quick Tech and every granted action), Stabilize
 * and Overcharge. The bespoke ones through their own resolution halves: the self actions
 * (`resolveSelfAction`: Hide, Disengage, Shut Down, Boot Up, the COMP/CON pair, Self-Destruct), the
 * contests (`resolveContestAction`: Search, the grapple break, contest and release) and the
 * drone/mine verbs (`resolveDeployAction`), and a move (`refereeForcedMove`: a `walk_token` inside
 * the move referee's checkpoint gather and post-move chain). The pilot-bridge and identity verbs (Mount, Jockey,
 * Ride, Dismount, Eject, Reload), Prepare and the lock bookkeeping are not forceable; they are
 * refused here with the reason.
 *
 * **Structural, never tactical.** Kept: the actor and every named target exist and are on the map,
 * a named weapon is one the attacker carries and is not destroyed, an activated action is one the
 * carrier carries, a deploy lands on a free cell, a mine verb names a mine. Skipped: everything
 * else — turn, budget, the once-per-turn ledger, the attack lock, statuses, range, line of sight,
 * cover, adjacency, Loading/Limited/Recharge, frequency caps, Ordnance, Hidden, immunities.
 *
 * **Costs** (`refereeIntent.ts`'s `SpendPolicy`): on the actor's own turn it spends the action and
 * stamps the ledger as the gameplay path prices it (a Superheavy weapon's shot at the Barrage's
 * Full action), floored at 0 by the reducer; off its turn it spends no action. Loading and
 * Recharge are consumed and Heat lands, because those are resolution steps; Limited and granted
 * pools are drawn, clamped at their cap. No attack lock opens. Every downstream trigger and
 * reaction fires as for a legal action, with its own normal legality (a target's Brace, an
 * Overwatch). A forced action never starts or folds the unit's activation (the room passes it).
 *
 * **A halt echoes the wrapper**, so the prompt's answer re-submits forced, and every forced plan's
 * primary entry is marked `forced` on its logged command and on `ResolutionAbout`.
 */
import { activeAttack, isItemDestroyed, weaponInstanceKey, type EncounterCommand, type EncounterState } from "@lancer-vtt/domain";
import { resolutionSource, type EncounterIntent, type GmForce, type RequestAttack, type RequestBarrage } from "@lancer-vtt/shared";
import { actionIdForIntent, checkSuperheavySkirmish, deployedActionRefusal, intentCost, isActivateActionLegal, legalTechAttackWeaponIds } from "../economy";
import { quickTechOption } from "../quickTech";
import { isMeleeActionAttack } from "../resolve/weapons";
import { resolveContestAction } from "./refereeContestAction";
import { resolveDeployAction, type RefereeDeployActionCtx } from "./refereeDeployAction";
import { createIntentLoop, isResolutionBroadcastRider, rejected, resolveIntentPlan, type GenericResolutionIntent, type IntentFacts, type IntentLoop, type RefereeIntentCtx, type SpendPolicy } from "./refereeIntent";
import { refereeForcedMove } from "./refereeMove";
import { resolveSelfAction } from "./refereeSelfAction";
import { observeTrackChanges } from "./trackObserver";
import type { RefereePlan } from "./types";

/** `refereeForce`'s context: the generic referee's, plus the compendium a forced gear-mine deploy reads. */
export type RefereeForceCtx = RefereeIntentCtx & Pick<RefereeDeployActionCtx, "compendium">;

export function refereeForce(state: EncounterState, wrapper: GmForce, callCtx: RefereeForceCtx): RefereePlan {
  // One loop for the forced intent, threaded to its resolution and to the track observer.
  const ctx = { ...callCtx, intentLoop: callCtx.intentLoop ?? createIntentLoop(wrapper.intent, callCtx.dice) };
  const plan = forcedPlan(state, wrapper.intent, ctx);
  if (plan.rejection !== undefined) return plan;
  if (plan.needsDecision !== undefined) {
    const echoed = plan.needsDecision.intent;
    const rewrapped: GmForce = { type: "gm_force", intent: echoed.type === "gm_force" ? echoed.intent : echoed };
    return { ...plan, needsDecision: { ...plan.needsDecision, intent: rewrapped } };
  }
  // A forced move is its own `walk_token`, already observed by the move referee; it has no
  // resolution to stamp.
  if (wrapper.intent.type === "request_move") return plan;
  return observeTrackChanges({ before: state, plan: stampForced(plan), ctx: { ...ctx, loop: ctx.intentLoop.loop } });
}

/** The forced resolution of `intent`, or a rejection naming why it can't resolve at all. */
function forcedPlan(state: EncounterState, inner: GmForce["intent"], ctx: RefereeForceCtx & { intentLoop: IntentLoop }): RefereePlan {
  const actor = resolutionSource(inner);
  const turn = state.activation.turn;
  const policy: SpendPolicy = { kind: "forced", onTurn: turn.phase === "acting" && turn.actor === actor };
  const refuse = (why: string): RefereePlan => rejected(state, `gm_force: ${why}`, "unresolvable");
  switch (inner.type) {
    case "request_attack":
    case "request_barrage":
    case "request_full_tech":
    case "request_activate":
    case "request_stabilize":
    case "request_overcharge": {
      if (inner.type === "request_attack" && inner.reaction === true) {
        return rejected(state, "gm_force: a forced shot is not a reaction — force it as a standard attack", "illegal");
      }
      // The room's server-stamped fields never ride a forced shot: Covering Fire's halving and the
      // Backswing follow-up are both rule outcomes this door does not adjudicate.
      const intent: GenericResolutionIntent = inner.type === "request_attack" ? { ...inner, damageScale: undefined, consumeReservation: undefined } : inner;
      const refusal = actorRefusal(state, actor) ?? genericRefusal(state, intent, ctx);
      if (refusal !== undefined) return refuse(refusal);
      return resolveIntentPlan({ state, intent, ctx, facts: forcedFacts(state, intent, ctx), policy });
    }
    case "request_hide":
    case "request_disengage":
    case "request_shutdown":
    case "request_boot_up":
    case "request_engage_compcon":
    case "request_take_back_compcon":
    case "request_self_destruct": {
      const refusal = actorRefusal(state, actor);
      return refusal !== undefined ? refuse(refusal) : resolveSelfAction({ state, intent: inner, ctx, policy, loop: ctx.intentLoop.loop });
    }
    case "request_search":
    case "request_grapple_break":
    case "request_grapple_contest":
    case "request_grapple_release": {
      const refusal = actorRefusal(state, actor) ?? (inner.type === "request_search" ? unitRefusal(state, inner.target) : undefined);
      return refusal !== undefined ? refuse(refusal) : resolveContestAction({ state, intent: inner, ctx, policy });
    }
    case "request_deploy_drone":
    case "request_deploy_mine":
    case "request_detect_mine":
    case "request_disarm_mine":
    case "request_detonate_mine": {
      const refusal = actorRefusal(state, actor);
      return refusal !== undefined ? refuse(refusal) : resolveDeployAction({ state, intent: inner, ctx, policy });
    }
    case "request_move":
      return refereeForcedMove(state, inner, ctx);
    default:
      return rejected(state, `gm_force: ${inner.type} can't be forced yet`, "illegal");
  }
}

/**
 * The turn facts the resolution reads, priced as the gameplay path would price the action
 * (`decisions/0209`: costs spend normally). A Superheavy weapon fires only in a Barrage, so its
 * forced attack costs the Barrage's Full action and stamps `barrage`, though it resolves as one
 * attack. No forced action continues a lock.
 */
function forcedFacts(state: EncounterState, intent: GenericResolutionIntent, ctx: RefereeIntentCtx): IntentFacts {
  const priceAs: GenericResolutionIntent | RequestBarrage =
    intent.type === "request_attack" && intent.tech !== true && intent.weapon !== undefined && !checkSuperheavySkirmish({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, verb: "skirmish" }).ok
      ? { type: "request_barrage", attacker: intent.attacker, weapon: intent.weapon, target: intent.target, accuracy: intent.accuracy }
      : intent;
  return {
    turn: state.activation.turn,
    lock: activeAttack(state.activation),
    midAttack: false,
    cost: intentCost(priceAs, ctx.overlay, state),
    actionId: actionIdForIntent(priceAs, { midAttack: false, followupReservation: false, state, overlay: ctx.overlay }),
  };
}

/** Why `id` can't act at all: it doesn't exist, or it isn't on the map. */
function actorRefusal(state: EncounterState, id: string): string | undefined {
  return unitRefusal(state, id);
}

function unitRefusal(state: EncounterState, id: string): string | undefined {
  const c = state.combatants[id];
  if (c === undefined) return `no combatant "${id}"`;
  return (c.pos ?? null) === null ? `${c.name} is not on the map` : undefined;
}

/** Why a generic-family intent can't resolve at all, or `undefined`. Only invariants a forced action still needs. */
function genericRefusal(state: EncounterState, intent: GenericResolutionIntent, ctx: RefereeIntentCtx): string | undefined {
  switch (intent.type) {
    case "request_attack":
    case "request_barrage": {
      if (intent.target.kind === "unit") {
        const refusal = unitRefusal(state, intent.target.id);
        if (refusal !== undefined) return refusal;
      }
      for (const also of intent.alsoTargets ?? []) {
        const refusal = unitRefusal(state, also);
        if (refusal !== undefined) return refusal;
      }
      return weaponRefusal(state, intent);
    }
    case "request_full_tech": {
      const refusal = unitRefusal(state, intent.target);
      if (refusal !== undefined) return refusal;
      const carried = quickTechOption(intent.option) !== undefined || isActivateActionLegal({ state, actorId: intent.actor, queriedId: intent.option, overlay: ctx.overlay });
      return carried ? undefined : `${state.combatants[intent.actor]!.name} has no tech option "${intent.option}"`;
    }
    case "request_activate": {
      if (intent.target !== undefined) {
        const refusal = unitRefusal(state, intent.target);
        if (refusal !== undefined) return refusal;
      }
      // Carrier ownership is structural: a unit can only be forced to use what it carries.
      if (!isActivateActionLegal({ state, actorId: intent.actor, queriedId: intent.action, overlay: ctx.overlay })) {
        return `${state.combatants[intent.actor]!.name} does not carry "${intent.action}"`;
      }
      return deployedActionRefusal({ state, actorId: intent.actor, action: intent.action, target: intent.target, overlay: ctx.overlay });
    }
    default:
      return undefined;
  }
}

/** Why the named weapon can't fire at all: not carried, or destroyed. A tech attack names an id the attacker can make. */
function weaponRefusal(state: EncounterState, intent: RequestAttack | RequestBarrage): string | undefined {
  const attacker = state.combatants[intent.attacker]!;
  const weapon = intent.weapon;
  const weapons = attacker.base.weapons ?? [];
  if (intent.type === "request_attack" && intent.tech === true) {
    const techWeapon = weapon ?? "act_invade";
    return legalTechAttackWeaponIds(state, intent.attacker).has(techWeapon) ? undefined : `${attacker.name} can't make the tech attack "${techWeapon}"`;
  }
  if (weapon === undefined) {
    return weapons.length >= 2 ? `${attacker.name} carries ${weapons.length} weapons — name one` : undefined;
  }
  if (isMeleeActionAttack(weapon)) return undefined;
  if (!weapons.some((w) => weaponInstanceKey(w) === weapon)) return `${attacker.name} carries no weapon "${weapon}"`;
  // A destroyed weapon is gone, not walled: nothing fires it.
  return isItemDestroyed(attacker.current.resources, weapon) ? `${attacker.name}'s weapon "${weapon}" is destroyed` : undefined;
}

/**
 * Mark the plan's primary entry forced: its first logged `apply_resolved` and its resolution
 * rider's `about`. Followups (reveals, destruction, triggers) are the action's consequences and
 * stay as they are. A deploy logs no `apply_resolved`; its rider still says so.
 */
function stampForced(plan: RefereePlan): RefereePlan {
  const [primary, ...rest] = plan.entries;
  if (primary === undefined) return plan;
  const rider = primary.broadcastRider;
  const stampedRider =
    rider !== undefined && isResolutionBroadcastRider(rider)
      ? { ...rider, resolution: { ...rider.resolution, ...(rider.resolution.about !== undefined ? { about: { ...rider.resolution.about, forced: true as const } } : {}) } }
      : rider;
  return { ...plan, entries: [{ ...primary, command: forcedCommand(primary.command), ...(stampedRider !== undefined ? { broadcastRider: stampedRider } : {}) }, ...rest] };
}

/** `command` with its first `apply_resolved` (itself, or inside a batch) marked `forced`. */
function forcedCommand(command: EncounterCommand): EncounterCommand {
  if (command.type === "apply_resolved") return { ...command, forced: true };
  if (command.type !== "batch") return command;
  const at = command.commands.findIndex((c) => c.type === "apply_resolved");
  if (at < 0) return command;
  return { ...command, commands: command.commands.map((c, i) => (i === at ? forcedCommand(c) : c)) };
}

/** The intent types {@link refereeForce} resolves. */
export type ForceableIntent = Extract<
  EncounterIntent,
  {
    type:
      | GenericResolutionIntent["type"]
      | "request_hide"
      | "request_disengage"
      | "request_shutdown"
      | "request_boot_up"
      | "request_engage_compcon"
      | "request_take_back_compcon"
      | "request_self_destruct"
      | "request_search"
      | "request_grapple_break"
      | "request_grapple_contest"
      | "request_grapple_release"
      | "request_deploy_drone"
      | "request_deploy_mine"
      | "request_detect_mine"
      | "request_disarm_mine"
      | "request_detonate_mine"
      | "request_move";
  }
>;

/**
 * The runtime twin of {@link ForceableIntent}, pinned both ways by tsc (a missing or extra key fails
 * to compile). The browser's override mode wraps exactly these.
 */
const FORCEABLE_INTENT_TYPES: Record<ForceableIntent["type"], true> = {
  request_attack: true,
  request_barrage: true,
  request_full_tech: true,
  request_activate: true,
  request_stabilize: true,
  request_overcharge: true,
  request_hide: true,
  request_disengage: true,
  request_shutdown: true,
  request_boot_up: true,
  request_engage_compcon: true,
  request_take_back_compcon: true,
  request_self_destruct: true,
  request_search: true,
  request_grapple_break: true,
  request_grapple_contest: true,
  request_grapple_release: true,
  request_deploy_drone: true,
  request_deploy_mine: true,
  request_detect_mine: true,
  request_disarm_mine: true,
  request_detonate_mine: true,
  request_move: true,
};

/** Whether a GM override can force `intent`: a forceable family, and not a reaction (a forced shot is never one). */
export function isForceableIntent(intent: EncounterIntent): intent is ForceableIntent {
  if (!Object.hasOwn(FORCEABLE_INTENT_TYPES, intent.type)) return false;
  return !(intent.type === "request_attack" && intent.reaction === true);
}
